import React, { useState, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  StyleSheet, 
  Pressable, 
  ActivityIndicator,
  Animated,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
  ScrollView
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Responsive breakpoints
const getBreakpoint = (width) => {
  if (width < 480) return 'mobile';
  if (width < 768) return 'mobileLarge';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Staccato cursor
const StaccatoCursor = ({ isDark }) => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const cursorColor = isDark ? '#E07A98' : '#C75B7A';

  useEffect(() => {
    if (!isWeb) return;
    const handleMouseMove = (e) => setPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    document.body.style.cursor = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.cursor = 'auto';
    };
  }, []);

  if (!isWeb) return null;

  return (
    <div style={{
      position: 'fixed', left: position.x - 4, top: position.y - 4,
      width: 8, height: 8, borderRadius: '50%', backgroundColor: cursorColor,
      pointerEvents: 'none', zIndex: 10000, boxShadow: `0 0 12px ${cursorColor}80`,
    }} />
  );
};

// Animated text
const AnimatedText = ({ children, style, delay = 0 }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(25)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 12, tension: 60, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return <Animated.Text style={[style, { opacity, transform: [{ translateY }] }]}>{children}</Animated.Text>;
};

// Animated section
const AnimatedSection = ({ children, delay = 0, style }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(30)).current;
  const scale = useRef(new Animated.Value(0.97)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 50, useNativeDriver: true }),
        Animated.spring(scale, { toValue: 1, friction: 10, tension: 40, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return <Animated.View style={[style, { opacity, transform: [{ translateY }, { scale }] }]}>{children}</Animated.View>;
};

// Button with hover
const AnimatedButton = ({ children, onPress, style, disabled }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onPressIn={() => Animated.spring(scale, { toValue: 0.97, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[style, { transform: [{ scale }] }]}>{children}</Animated.View>
    </Pressable>
  );
};

// Google logo SVG
const GoogleLogo = () => {
  if (!isWeb) {
    return <Text style={{ fontSize: 18, fontWeight: '700' }}>G</Text>;
  }
  
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
};

// Floating music note
const FloatingNote = ({ style, delay, theme }) => {
  const translateY = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(opacity, { toValue: 0.25, duration: 700, delay, useNativeDriver: true }).start();
    Animated.loop(Animated.sequence([
      Animated.timing(translateY, { toValue: -15, duration: 2500, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 15, duration: 2500, useNativeDriver: true }),
    ])).start();
  }, []);

  return <Animated.View style={[styles.floatingNote, style, { opacity, transform: [{ translateY }], backgroundColor: theme.primary }]} />;
};

// Get dark mode from localStorage
const getDarkMode = () => {
  if (isWeb && typeof localStorage !== 'undefined') {
    return localStorage.getItem('sheetshed_darkmode') === 'true';
  }
  return false;
};

export const AuthScreen = ({ navigation, route }) => {
  const [isDark, setIsDark] = useState(getDarkMode());
  const theme = isDark ? baseTheme.dark : baseTheme.light;
  const initialMode = route?.params?.mode || 'signin';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);

  const { signIn, signUp, signInWithGoogle } = useAuth();

  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  // Listen for dark mode changes from other tabs/windows
  useEffect(() => {
    if (!isWeb) return;
    const handleStorage = (e) => {
      if (e.key === 'sheetshed_darkmode') {
        setIsDark(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Set page title
  useEffect(() => {
    if (isWeb && typeof document !== 'undefined') {
      document.title = `${mode === 'signin' ? 'Sign In' : 'Sign Up'} - SheetShed`;
    }
  }, [mode]);

  // Listen for resize
  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!displayName.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        await signUp(email, password, displayName);
      } else {
        await signIn(email, password);
      }
      navigation.replace('Library');
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigation.replace('Library');
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use': return 'This email is already registered';
      case 'auth/invalid-email': return 'Invalid email address';
      case 'auth/weak-password': return 'Password should be at least 6 characters';
      case 'auth/user-not-found':
      case 'auth/wrong-password': return 'Invalid email or password';
      default: return 'Something went wrong. Please try again.';
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError('');
  };

  const getPadding = () => {
    if (breakpoint === 'mobile') return 20;
    if (breakpoint === 'mobileLarge') return 28;
    return 32;
  };

  return (
    <KeyboardAvoidingView style={[styles.container, { backgroundColor: theme.background }]} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <StaccatoCursor isDark={isDark} />

      {/* Floating decorations */}
      <FloatingNote style={{ top: '10%', left: '5%' }} delay={200} theme={theme} />
      <FloatingNote style={{ top: '20%', right: '10%' }} delay={400} theme={theme} />
      {!isMobile && (
        <>
          <FloatingNote style={{ bottom: '30%', left: '8%' }} delay={600} theme={theme} />
          <FloatingNote style={{ bottom: '15%', right: '5%' }} delay={800} theme={theme} />
        </>
      )}

      {/* Back Button — uses Pressable directly so position:absolute applies to the clickable element */}
      <Pressable
        onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Landing')}
        style={[styles.backButton, { top: isMobile ? 20 : 32, left: isMobile ? 20 : 32 }]}
      >
        <Text style={[styles.backArrowText, { color: theme.textSecondary, fontSize: isMobile ? 16 : 18 }]}>←</Text>
        <Text style={[styles.backButtonText, { color: theme.textSecondary }]}>Back</Text>
      </Pressable>

      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        <View style={[styles.content, { padding: getPadding() }]}>
          {/* Logo */}
          <AnimatedSection delay={0} style={styles.logoContainer}>
            <View style={[styles.logoIcon, { backgroundColor: theme.primary }]}>
              <View style={[styles.logoNote, { backgroundColor: theme.textOnPrimary }]} />
              <View style={[styles.logoNote2, { backgroundColor: theme.textOnPrimary }]} />
            </View>
            <Text style={[styles.logoText, { color: theme.textPrimary, fontSize: isMobile ? 26 : 30 }]}>SheetShed</Text>
          </AnimatedSection>

          {/* Form Card */}
          <AnimatedSection delay={100} style={[styles.formCard, { backgroundColor: theme.surface, padding: isMobile ? 28 : 44, maxWidth: isMobile ? '100%' : 460 }]}>
            <AnimatedText style={[styles.title, { color: theme.textPrimary, fontSize: isMobile ? 26 : 30 }]} delay={150}>
              {mode === 'signin' ? 'Welcome back' : 'Create account'}
            </AnimatedText>
            <AnimatedText style={[styles.subtitle, { color: theme.textSecondary, fontSize: isMobile ? 15 : 17 }]} delay={200}>
              {mode === 'signin' ? 'Sign in to access your sheet music' : 'Start organizing your sheet music today'}
            </AnimatedText>

            {/* Google Sign In */}
            <AnimatedSection delay={250}>
              <AnimatedButton onPress={handleGoogleSignIn} disabled={loading} style={[styles.googleButton, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                <View style={styles.googleIconContainer}>
                  <GoogleLogo />
                </View>
                <Text style={[styles.googleButtonText, { color: theme.textPrimary, fontSize: isMobile ? 15 : 17 }]}>Continue with Google</Text>
              </AnimatedButton>
            </AnimatedSection>

            {/* Divider */}
            <AnimatedSection delay={300} style={styles.divider}>
              <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
              <Text style={[styles.dividerText, { color: theme.textMuted }]}>or</Text>
              <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
            </AnimatedSection>

            {/* Form Fields */}
            {mode === 'signup' && (
              <AnimatedSection delay={350} style={styles.inputGroup}>
                <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Name</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: focusedField === 'name' ? theme.primary : theme.border, color: theme.textPrimary }]}
                  placeholder="Your name" placeholderTextColor={theme.textMuted} value={displayName} onChangeText={setDisplayName}
                  autoCapitalize="words" onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)}
                />
              </AnimatedSection>
            )}

            <AnimatedSection delay={mode === 'signup' ? 400 : 350} style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Email</Text>
              <TextInput
                style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: focusedField === 'email' ? theme.primary : theme.border, color: theme.textPrimary }]}
                placeholder="you@example.com" placeholderTextColor={theme.textMuted} value={email} onChangeText={setEmail}
                keyboardType="email-address" autoCapitalize="none" autoComplete="email" onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
              />
            </AnimatedSection>

            <AnimatedSection delay={mode === 'signup' ? 450 : 400} style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: theme.textSecondary }]}>Password</Text>
              <TextInput
                style={[styles.input, { backgroundColor: theme.backgroundAlt, borderColor: focusedField === 'password' ? theme.primary : theme.border, color: theme.textPrimary }]}
                placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'} placeholderTextColor={theme.textMuted} value={password} onChangeText={setPassword}
                secureTextEntry onFocus={() => setFocusedField('password')} onBlur={() => setFocusedField(null)}
              />
            </AnimatedSection>

            {/* Error Message */}
            {error ? (
              <AnimatedSection delay={0} style={[styles.errorContainer, { backgroundColor: `${theme.error}15`, borderColor: `${theme.error}30` }]}>
                <Text style={[styles.errorText, { color: theme.error }]}>{error}</Text>
              </AnimatedSection>
            ) : null}

            {/* Submit Button */}
            <AnimatedSection delay={mode === 'signup' ? 500 : 450}>
              <AnimatedButton onPress={handleSubmit} disabled={loading} style={[styles.submitButton, { backgroundColor: theme.primary }, loading && styles.submitButtonDisabled]}>
                {loading ? (
                  <ActivityIndicator color={theme.textOnPrimary} />
                ) : (
                  <Text style={[styles.submitButtonText, { color: theme.textOnPrimary, fontSize: isMobile ? 16 : 17 }]}>{mode === 'signin' ? 'Sign In' : 'Create Account'}</Text>
                )}
              </AnimatedButton>
            </AnimatedSection>

            {/* Toggle Mode */}
            <AnimatedSection delay={mode === 'signup' ? 550 : 500} style={[styles.toggleContainer, { flexDirection: isMobile ? 'column' : 'row' }]}>
              <Text style={[styles.toggleText, { color: theme.textSecondary }]}>{mode === 'signin' ? "Don't have an account?" : 'Already have an account?'}</Text>
              <AnimatedButton onPress={toggleMode}>
                <Text style={[styles.toggleLink, { color: theme.primary }]}>{mode === 'signin' ? 'Sign up' : 'Sign in'}</Text>
              </AnimatedButton>
            </AnimatedSection>
          </AnimatedSection>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, position: 'relative' },
  scrollContent: { flexGrow: 1 },
  floatingNote: { position: 'absolute', width: 12, height: 12, borderRadius: 6, zIndex: 0 },
  backButton: { position: 'absolute', flexDirection: 'row', alignItems: 'center', gap: 10, zIndex: 10, padding: 12 },
  backArrowText: {},
  backButtonText: { fontSize: 17 },
  content: { flex: 1, justifyContent: 'center', alignItems: 'center', zIndex: 1, minHeight: '100%' },
  logoContainer: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 36 },
  logoIcon: { width: 48, height: 48, borderRadius: baseTheme.borderRadius.xl, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  logoNote: { width: 11, height: 11, borderRadius: 6, position: 'absolute', top: 13, left: 13 },
  logoNote2: { width: 9, height: 9, borderRadius: 5, position: 'absolute', bottom: 13, right: 13 },
  logoText: { fontWeight: baseTheme.typography.fontWeight.bold },
  formCard: { width: '100%', borderRadius: baseTheme.borderRadius.xxxl, ...Platform.select({ web: { boxShadow: baseTheme.shadows.lg } }) },
  title: { fontWeight: baseTheme.typography.fontWeight.bold, marginBottom: 10 },
  subtitle: { marginBottom: 32 },
  googleButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderRadius: baseTheme.borderRadius.pill, padding: 16, borderWidth: 1.5, gap: 14 },
  googleIconContainer: { width: 24, height: 24, justifyContent: 'center', alignItems: 'center' },
  googleButtonText: { fontWeight: baseTheme.typography.fontWeight.medium },
  divider: { flexDirection: 'row', alignItems: 'center', marginVertical: 24, gap: 18 },
  dividerLine: { flex: 1, height: 1 },
  dividerText: { fontSize: 14 },
  inputGroup: { marginBottom: 18 },
  inputLabel: { fontSize: 14, fontWeight: baseTheme.typography.fontWeight.medium, marginBottom: 10 },
  input: { borderRadius: baseTheme.borderRadius.xl, padding: 16, fontSize: 16, borderWidth: 2, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  errorContainer: { borderRadius: baseTheme.borderRadius.lg, padding: 14, marginBottom: 18, borderWidth: 1 },
  errorText: { fontSize: 14, textAlign: 'center' },
  submitButton: { borderRadius: baseTheme.borderRadius.pill, padding: 18, alignItems: 'center', marginTop: 10 },
  submitButtonDisabled: { opacity: 0.6 },
  submitButtonText: { fontWeight: baseTheme.typography.fontWeight.semibold },
  toggleContainer: { justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 },
  toggleText: { fontSize: 15 },
  toggleLink: { fontSize: 15, fontWeight: baseTheme.typography.fontWeight.semibold },
});

export default AuthScreen;
